import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Calendar, MapPin, ArrowRight, Sparkles, Users } from "lucide-react";
import EventsPage from "./EventsPage";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

function FeaturedSymposiumCard() {
  const [content, setContent] = useState(null);
  useEffect(() => {
    api.get("/symposium-content").then(({ data }) => setContent(data)).catch(() => {});
  }, []);
  if (!content) return null;

  return (
    <section className="ser-section pb-0" data-testid="featured-symposium">
      <div className="ser-container">
        <Link
          to="/conferences/symposium-2026"
          className="ser-card group block overflow-hidden md:flex md:flex-row"
          style={{ borderLeft: "4px solid var(--ser-primary)" }}
          data-testid="featured-symposium-card"
        >
          <div className="md:w-2/5 h-56 md:h-auto overflow-hidden bg-[var(--ser-section-alt)]">
            <img
              src={content.hero_image}
              alt={content.name}
              className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
            />
          </div>
          <div className="p-7 md:p-10 md:w-3/5 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4" style={{ color: "var(--ser-primary)" }} />
              <span className="ser-tag">Featured Symposium</span>
            </div>
            <h2
              className="font-serif-display text-2xl md:text-3xl uppercase mb-3"
              style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}
            >
              {content.name}
            </h2>
            <p className="text-sm mb-5 leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
              {content.tagline}
            </p>
            <div className="flex flex-wrap gap-4 text-xs mb-6" style={{ color: "var(--ser-text-muted)" }}>
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {content.dates}</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {content.venue_name}, {content.venue_location}</span>
            </div>
            <div className="mt-auto flex flex-wrap items-center gap-3">
              <span className="btn-ser-primary" data-testid="featured-view-btn">
                Symposium details <ArrowRight className="w-3.5 h-3.5" />
              </span>
              <span className="text-xs" style={{ color: "var(--ser-text-muted)" }}>
                Abstract deadline: {formatDate(content.abstract_deadline)}
              </span>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}

function FeaturedAGMCard() {
  const [agmEvent, setAgmEvent] = useState(null);
  useEffect(() => {
    api.get("/events").then(({ data }) => {
      const agm = data.find((e) =>
        e.title.toLowerCase().includes("annual general meeting") ||
        e.title.toLowerCase().includes("agm")
      );
      if (agm) setAgmEvent(agm);
    }).catch(() => {});
  }, []);
  if (!agmEvent) return null;

  return (
    <section className="ser-section pb-0" data-testid="featured-agm">
      <div className="ser-container">
        <Link
          to="/events"
          className="ser-card group block overflow-hidden md:flex md:flex-row"
          style={{ borderLeft: "4px solid var(--ser-clay)" }}
          data-testid="featured-agm-card"
        >
          <div className="md:w-2/5 h-56 md:h-auto max-h-[400px] overflow-hidden" style={{ backgroundColor: "var(--ser-primary-dark)" }}>
            <img
              src={agmEvent.image_url}
              alt={agmEvent.title}
              className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
            />
          </div>
          <div className="p-7 md:p-10 md:w-3/5 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4" style={{ color: "var(--ser-clay)" }} />
              <span className="ser-tag" style={{ backgroundColor: "var(--ser-clay)", color: "white" }}>Annual General Meeting</span>
            </div>
            <h2
              className="font-serif-display text-2xl md:text-3xl uppercase mb-3"
              style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}
            >
              {agmEvent.title}
            </h2>
            <p className="text-sm mb-5 leading-relaxed" style={{ color: "var(--ser-text-muted)" }}>
              {agmEvent.description}
            </p>
            <div className="flex flex-wrap gap-4 text-xs mb-6" style={{ color: "var(--ser-text-muted)" }}>
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {formatDate(agmEvent.date)}</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {agmEvent.location}</span>
            </div>
            <div className="mt-auto flex flex-wrap items-center gap-3">
              <span className="btn-ser-primary" style={{ backgroundColor: "var(--ser-clay)" }} data-testid="featured-agm-view-btn">
                View details <ArrowRight className="w-3.5 h-3.5" />
              </span>
              <span className="text-xs" style={{ color: "var(--ser-text-muted)" }}>
                Also streaming online via Zoom
              </span>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}

export default function ConferencesPage() {
  return (
    <>
      <FeaturedSymposiumCard />
      <FeaturedAGMCard />
      <EventsPage
        forcedCategory="Conference"
        pageEyebrow="Conferences"
        pageTitle="SER Africa Conferences"
        pageSubtitle="Discover upcoming and past conferences advancing restoration science, practice, and policy across Africa."
        testIdRoot="conferences-page"
      />
    </>
  );
}
